import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

function scrollToContact() {
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const solutions = [
  {
    slug: "tratax-one",
    tag: "Healthcare · Flagship",
    name: "TrataX One",
    subtitle: "Complete hospital management platform.",
    description:
      "A unified operating system for mid-sized hospitals and multi-speciality clinics — replacing fragmented OPD, IPD, pharmacy and billing tools with one calm, role-based interface.",
    modules: [
      "Patient Management",
      "OPD / IPD",
      "Billing & Insurance",
      "Pharmacy",
      "Laboratory",
      "Telemedicine",
      "Doctor Scheduling",
      "Reports & Audit",
    ],
    stack: ["React", "FastAPI", "MongoDB", "WebRTC", "Stripe"],
    outcome: { value: "60%", label: "Less time per patient discharge" },
    image:
      "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?auto=format&fit=crop&w=1600&q=80",
  },
  {
    slug: "churchflow",
    tag: "Community · Live",
    name: "ChurchFlow",
    subtitle: "Church CRM & member management.",
    description:
      "Pastors, admins and volunteers run the entire life of a congregation from one place — members, services, giving, events and communication on web and mobile.",
    modules: [
      "Member Directory",
      "Service Planning",
      "Giving & Tithes",
      "Events & RSVPs",
      "Small Groups",
      "Bulk SMS / WhatsApp",
      "Volunteer Roster",
      "Reports",
    ],
    stack: ["React", "Node", "MongoDB", "Razorpay", "Twilio"],
    outcome: { value: "3×", label: "Higher member engagement vs spreadsheets" },
    image:
      "https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?auto=format&fit=crop&w=1600&q=80",
  },
  {
    slug: "heritex-ai",
    tag: "AI · In-house Practice",
    name: "Heritex AI",
    subtitle: "AI automation for ambitious businesses.",
    description:
      "Our in-house AI consulting practice — we design, build and deploy custom AI agents, chatbots and workflow automations for startups and enterprises that want compounding leverage, not toy demos.",
    modules: [
      "Custom AI Agents",
      "RAG Knowledge Bases",
      "WhatsApp / Web Chatbots",
      "Workflow Automation",
      "Voice Agents",
      "AI Strategy Audits",
      "Prompt Engineering",
      "LLM Fine-tuning",
    ],
    stack: ["GPT-5.2", "Claude 4.5", "Gemini 3", "LangChain", "Pinecone"],
    outcome: { value: "40 hrs", label: "Avg. weekly hours saved per client" },
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1600&q=80",
  },
  {
    slug: "actogram",
    tag: "Social · Media & Entertainment",
    name: "Actogram",
    subtitle: "The professional social network for media & entertainment.",
    description:
      "A LinkedIn-style network purpose-built for actors, filmmakers, crew and studios — discover talent, manage portfolios, audition and collaborate without the noise of generic platforms.",
    modules: [
      "Talent Profiles & Reels",
      "Casting Calls",
      "Audition Submissions",
      "Crew Directory",
      "Project Boards",
      "Messaging & DMs",
      "Verified Studios",
      "Subscription Plans",
    ],
    stack: ["React Native", "FastAPI", "MongoDB", "S3", "Stripe"],
    outcome: { value: "2k+", label: "Industry profiles onboarded at launch" },
    image:
      "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1600&q=80",
  },
  {
    slug: "custom-erp",
    tag: "Enterprise · Made-to-measure",
    name: "Custom ERP",
    subtitle: "Tailor-made business systems.",
    description:
      "When off-the-shelf ERPs fight your workflow, we build one around it instead. Inventory, sales, procurement, manufacturing and finance — modular, role-based, and yours.",
    modules: [
      "Inventory & Warehouse",
      "Sales & CRM",
      "Procurement",
      "Manufacturing",
      "Finance & GST",
      "HR & Payroll",
      "Multi-branch",
      "Approval Workflows",
    ],
    stack: ["React", "FastAPI", "PostgreSQL", "Redis", "Tally Connect"],
    outcome: { value: "80%", label: "Manual data entry eliminated" },
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1600&q=80",
  },
  {
    slug: "ecommerce",
    tag: "Commerce · Storefronts",
    name: "E-Commerce Storefronts",
    subtitle: "Premium online stores that actually convert.",
    description:
      "Custom storefronts, marketplaces and D2C platforms — fast on mobile, ranked on Google, integrated with payments, shipping and your back-office from day one.",
    modules: [
      "Catalogue & Variants",
      "Cart & Checkout",
      "Payments (UPI / Stripe)",
      "Shipping & COD",
      "Subscriptions",
      "Coupons & Loyalty",
      "Admin Dashboard",
      "SEO & Analytics",
    ],
    stack: ["Next.js", "Node", "MongoDB", "Stripe", "Razorpay", "Shiprocket"],
    outcome: { value: "2.4×", label: "Avg. lift in mobile conversion rate" },
    image:
      "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&w=1600&q=80",
  },
];

const stats = [
  { value: "6+", label: "Flagship solutions built" },
  { value: "8+", label: "Industries served" },
  { value: "20+", label: "Custom builds shipped" },
  { value: "100%", label: "Founder-led delivery" },
];

const industries = [
  "Healthcare & Hospitals",
  "Faith & Community",
  "Media & Entertainment",
  "E-Commerce & Retail",
  "Education & Schools",
  "AI & Automation",
  "Hospitality & Travel",
  "Finance & Fintech",
];

export default function Portfolio() {
  return (
    <section
      id="portfolio"
      data-testid="portfolio"
      className="relative py-24 md:py-36 bg-[#FDFCFB]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
          <div>
            <div className="caption mb-6">Featured Solutions</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#1A1A1A] leading-[1.05] max-w-3xl">
              Real products. <span className="italic">Live businesses.</span>
            </h2>
          </div>
          <p className="max-w-md text-[#6B6A68] md:text-right">
            Each of these was designed, engineered and shipped end-to-end by
            Heritex. Some are our own products, others built for clients under
            NDA — modules and outcomes are real.
          </p>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-y border-[#E8E6E1] py-10 md:py-12 mb-20 md:mb-28">
          {stats.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="font-serif-display text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">
                {s.value}
              </div>
              <div className="mt-2 caption !text-[11px]">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Solutions — alternating editorial layout */}
        <div className="space-y-24 md:space-y-32">
          {solutions.map((p, i) => {
            const reverse = i % 2 === 1;
            return (
              <motion.article
                key={p.slug}
                data-testid={`solution-${p.slug}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8 }}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center"
              >
                {/* Image */}
                <div className={`md:col-span-6 ${reverse ? "md:order-2" : ""}`}>
                  <div className="relative w-full aspect-[4/3] md:aspect-[5/4] overflow-hidden rounded-2xl bg-[#F5F4F0] group">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                    />
                    <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#FDFCFB]/85 backdrop-blur-sm text-[10px] tracking-[0.18em] uppercase font-mono-tech text-[#1A1A1A]">
                      {p.tag}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={`md:col-span-6 ${reverse ? "md:order-1" : ""}`}>
                  <div className="caption mb-4">/ 0{i + 1} — {p.tag.split(" · ")[0]}</div>
                  <h3 className="font-serif-display text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#1A1A1A] leading-[1.05]">
                    {p.name}
                  </h3>
                  <p className="mt-3 font-serif-display italic text-lg md:text-xl text-[#1A1A1A]/80">
                    {p.subtitle}
                  </p>
                  <p className="mt-5 text-[#6B6A68] leading-relaxed text-[15px]">
                    {p.description}
                  </p>

                  {/* Modules */}
                  <div className="mt-7">
                    <div className="caption mb-3">Modules</div>
                    <div className="flex flex-wrap gap-2">
                      {p.modules.map((m) => (
                        <span
                          key={m}
                          className="px-3 py-1.5 rounded-full border border-[#E8E6E1] bg-[#F5F4F0] text-[12px] text-[#1A1A1A]"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Stack */}
                  <div className="mt-6">
                    <div className="caption mb-3">Tech Stack</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-2">
                      {p.stack.map((s, idx) => (
                        <span
                          key={s}
                          className="text-[12px] font-mono-tech text-[#1A1A1A]/85"
                        >
                          {s}
                          {idx < p.stack.length - 1 && (
                            <span className="text-[#1A1A1A]/30 ml-3">·</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Outcome + CTA */}
                  <div className="mt-8 pt-6 border-t border-[#E8E6E1] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                    <div>
                      <div className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A] tracking-tight leading-none">
                        {p.outcome.value}
                      </div>
                      <div className="mt-1.5 caption !text-[11px]">{p.outcome.label}</div>
                    </div>
                    <button
                      data-testid={`solution-cta-${p.slug}`}
                      onClick={scrollToContact}
                      className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm hover:bg-[#333] transition-all self-start sm:self-auto"
                    >
                      Enquire about {p.name.split(" ")[0]}
                      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* Industries Served */}
        <div className="mt-28 md:mt-36 pt-16 md:pt-20 border-t border-[#E8E6E1]">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <div className="caption mb-6">Industries Served</div>
              <h3 className="font-serif-display text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#1A1A1A] leading-[1.05] max-w-3xl">
                We've shipped across <span className="italic">eight industries.</span>
              </h3>
            </div>
            <p className="max-w-sm text-[#6B6A68] md:text-right">
              Not on the list? We've probably built something close. Tell us
              what you're working on.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {industries.map((ind, i) => (
              <motion.div
                key={ind}
                data-testid={`industry-${ind.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.04 }}
                className="px-5 py-5 md:py-6 rounded-xl border border-[#E8E6E1] bg-[#FDFCFB] hover:border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FDFCFB] transition-all duration-500 group cursor-default"
              >
                <div className="font-mono-tech text-[10px] tracking-[0.18em] text-[#6B6A68] group-hover:text-[#FDFCFB]/60 mb-2">
                  / {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-serif-display text-lg md:text-xl tracking-tight">
                  {ind}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
